import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Unlink } from "lucide-react";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import { saveSupplierSettings, unlinkSupplier } from "services/invoice-inbox.service";
import InvoiceSupplierCreate from "./invoice-supplier-create";
import { FieldSuggestion, SuggestionAccess, reviewMarkClass } from "./invoice-doc-suggestion";
import { Card, RunChange, ToggleSwitch, buttonClass } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  busy: boolean;
  /** Same right the supplier settings screen requires — reaching the register
   * is not enough to change how a whole counterparty's future invoices get
   * approved. */
  canManageSettings: boolean;
  run: RunChange;
  reloadAfterChange: () => Promise<void>;
  suggestions: SuggestionAccess;
};

/** Who sent the invoice: the linked customer card, or — before it is linked —
 * what the document says, with the way to create or link the supplier. */
export default function InvoiceDocSupplierCard(props: Props) {
  const { t } = useTranslation();
  return (
    <Card title={t("supplier", { defaultValue: "Supplier" })}>
      {props.detail.supplierCustomer ? <LinkedSupplier {...props} /> : <UnlinkedSupplier {...props} />}
    </Card>
  );
}

function LinkedSupplier({ companyId, detail, busy, canManageSettings, run }: Props) {
  const { t } = useTranslation();
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const { doc } = detail;
  const supplier = detail.supplierCustomer!;
  // A wrong link can be taken back only before anybody is asked to approve the
  // document — the server refuses it afterwards (SUPPLIER_LOCKED).
  const canUnlink = !doc.deletedAt && (doc.status === "new" || doc.status === "needs_review");
  const codes = [supplier.companyCode, supplier.vatCode].filter(Boolean).join(" · ");

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <Link to={`/app/${companyId}/customers/${supplier.id}`} className="font-medium text-indigo-600 hover:underline">
          {supplier.name}
        </Link>
        {codes && <span className="text-xs text-gray-400">{codes}</span>}
      </div>

      {canUnlink && !confirmingUnlink && (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-700 hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={() => setConfirmingUnlink(true)}
        >
          <Unlink size={12} aria-hidden="true" />
          {t("invoice_inbox_supplier_unlink", { defaultValue: "Wrong supplier? Unlink" })}
        </button>
      )}
      {canUnlink && confirmingUnlink && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-sm">
          <p className="text-amber-800">
            {t("invoice_inbox_supplier_unlink_confirm", {
              defaultValue:
                "Unlink this supplier? Invoices with this supplier name will no longer be linked to it automatically.",
            })}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="rounded bg-amber-600 px-3 py-1 text-white hover:bg-amber-700 disabled:opacity-50"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await unlinkSupplier(companyId, doc.id);
                  setConfirmingUnlink(false);
                })
              }
            >
              {t("invoice_inbox_supplier_unlink_yes", { defaultValue: "Unlink" })}
            </button>
            <button type="button" className={buttonClass} disabled={busy} onClick={() => setConfirmingUnlink(false)}>
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      )}

      {canManageSettings && (
        <div className="border-t border-gray-100 pt-1">
          <ToggleSwitch
            label={t("invoice_inbox_supplier_invoices_arrive_paid", {
              defaultValue: "Invoices from this supplier arrive already paid",
            })}
            help={t("invoice_inbox_supplier_invoices_arrive_paid_help", {
              defaultValue:
                "Changes how invoices from this supplier are approved from now on — the manager's approval step is skipped, the accountant's check still applies. Documents already in the register are not affected.",
            })}
            checked={Boolean(supplier.invoicesArrivePaid)}
            disabled={busy}
            onChange={(checked) =>
              run(() => saveSupplierSettings(companyId, supplier.id, { invoicesArrivePaid: checked }))
            }
          />
          <ToggleSwitch
            label={t("invoice_inbox_supplier_expects_monthly", { defaultValue: "This supplier invoices every month" })}
            help={t("invoice_inbox_supplier_expects_monthly_help", {
              defaultValue:
                "The register will warn when a month's invoice from this supplier has not arrived by the day it usually comes.",
            })}
            checked={Boolean(supplier.expectsMonthlyInvoice)}
            disabled={busy}
            onChange={(checked) =>
              run(() => saveSupplierSettings(companyId, supplier.id, { expectsMonthlyInvoice: checked }))
            }
          />
        </div>
      )}
    </div>
  );
}

function UnlinkedSupplier({ companyId, detail, canManageSettings, reloadAfterChange, suggestions }: Props) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const { doc } = detail;

  // The document's own words for each supplier field, each with whatever the
  // reader could not confirm underneath it.
  const rawField = (column: keyof typeof doc, label: string | null, mono = false) => (
    <div key={column}>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${reviewMarkClass(doc, column)}`}>
        {label && <span className="text-gray-500">{label}: </span>}
        {(doc[column] as string | null) || "—"}
      </div>
      <FieldSuggestion doc={doc} column={column} value={doc[column]} access={suggestions} />
    </div>
  );

  return (
    <div className="space-y-1">
      {rawField("supplierNameRaw", null)}
      {rawField("supplierRegCodeRaw", t("company_code", { defaultValue: "Company code" }))}
      {rawField("supplierVatCodeRaw", t("vat_code", { defaultValue: "VAT code" }))}
      {rawField("supplierIbanRaw", null, true)}
      <p className="text-xs text-amber-700">
        {t("invoice_inbox_supplier_unlinked", { defaultValue: "This supplier is not linked to a customer card yet." })}
      </p>
      {!doc.deletedAt && !creating && (
        <button type="button" className={buttonClass} onClick={() => setCreating(true)}>
          {t("invoice_inbox_create_supplier", { defaultValue: "Create supplier" })}
        </button>
      )}
      {creating && (
        <InvoiceSupplierCreate
          companyId={companyId}
          detail={detail}
          canManageSettings={canManageSettings}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reloadAfterChange();
          }}
        />
      )}
    </div>
  );
}
